import { Box, HStack, Text, VStack } from "@chakra-ui/react"
import { energy } from "@hydrologiq/common/transformations/data/energy"
import { EnergyUnit } from "@hydrologiq/common/types/energy/consumption"
import { Tooltip } from "@hydrologiq/shared/chakra/Tooltip"
import { LoadPrecedenceEntry, LoadPrecedenceState } from "../../../types/SystemView"
import { NODE_ROW_HEIGHT } from "../../../types/SystemNode"

// Configuration State palette (docs/architecture/ui-visual-vocabulary.md, D): counted is Active, left out is Inactive.
const STATES: Record<LoadPrecedenceState, { label: string; bg: string; color: string }> = {
  inSiteLoad: { label: "Reported As Site Load", bg: "#C6F6D5", color: "#22543D" },
  notInSiteLoad: { label: "Not Reported As Site Load", bg: "#EDF2F7", color: "#718096" },
  notReporting: { label: "No Data", bg: "#EDF2F7", color: "#718096" },
}

function explain(entry: LoadPrecedenceEntry): string {
  const isAcLoad = entry.connectionName === null
  if (entry.state === "notReporting") {
    return isAcLoad
      ? "this AC Load sent nothing in this interval, so the inverter/charger output it covers is reported instead"
      : "nothing was received for this output in this interval"
  }
  if (entry.state === "notInSiteLoad") {
    return `${entry.coveredBy?.name ?? "an AC Load"} already reports this power, so it isn't counted twice. This Asset's own page still shows its full output`
  }
  return isAcLoad
    ? "this AC Load's power is this System's site load"
    : "no AC Load reported this power, so this output is counted"
}

function label(entry: LoadPrecedenceEntry, showAssetNames: boolean): string {
  if (entry.connectionName === null) return showAssetNames ? entry.assetName : "Power"
  return showAssetNames ? `${entry.assetName} · ${entry.connectionName}` : entry.connectionName
}

/** One entry's power as a `name: value` property, coloured by its state; the tooltip says what the colour means. */
export function LoadPrecedenceValue({ name, entry }: { name: string; entry: LoadPrecedenceEntry }) {
  const state = STATES[entry.state]
  const description = `${state.label}: ${explain(entry)}`
  return (
    <HStack fontSize="8px" width="100%">
      <Tooltip content={description} positioning={{ placement: "bottom-end" }}>
        {/* pointerEvents: React Flow sets pointer-events: none on locked nodes, so without this the hover falls through to the canvas */}
        <HStack pointerEvents="all" cursor="help" aria-label={description} bg={state.bg} color={state.color} borderRadius="3px" padding="0 2px" gap={0.5} minWidth={0}>
          <Text truncate>{name}:</Text>
          <Text fontWeight="bold" flexShrink={0}>
            {/* Unavailable (CONTEXT.md): a dash, never zero */}
            {entry.powerAverage === null ? "—" : `${energy(EnergyUnit.W, EnergyUnit.kW, entry.powerAverage).toFixed(2)} kW`}
          </Text>
        </HStack>
      </Tooltip>
    </HStack>
  )
}

export interface LoadPrecedenceRowsProps {
  /** The entries the node's Power attribute doesn't show. */
  entries: LoadPrecedenceEntry[]
  /** True on an Asset Group's node, whose entries belong to its Modules. */
  showAssetNames?: boolean
}

/** What Load Precedence decided in the latest bucket, read from getSystemView, as properties under a node's attributes. */
function LoadPrecedenceRows({ entries, showAssetNames = false }: LoadPrecedenceRowsProps) {
  return (
    <VStack alignItems="stretch" gap={0}>
      {entries.map((entry) => (
        <Box key={`${entry.assetId}:${entry.connectionName ?? ""}`} height={`${NODE_ROW_HEIGHT}px`}>
          <LoadPrecedenceValue name={label(entry, showAssetNames)} entry={entry} />
        </Box>
      ))}
    </VStack>
  )
}

export default LoadPrecedenceRows
